import React from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { KANBAN_COLUMNS, STATUSES, PRIORITIES, fmtDate, isOverdue } from "../lib/constants";
import { PriorityBadge, ProgressBar, Avatar } from "./Badges";
import { AlertTriangle, Clock, Lock, Bug } from "lucide-react";

export default function KanbanBoard({ devs, onMove, onCardClick, canWrite }) {
  const grouped = {};
  KANBAN_COLUMNS.forEach((c) => (grouped[c] = []));
  devs.forEach((d) => {
    if (grouped[d.status]) grouped[d.status].push(d);
  });

  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;
    onMove(draggableId, destination.droppableId);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-0 overflow-x-auto kanban-scroll border-t border-l border-border" data-testid="kanban-board">
        {KANBAN_COLUMNS.map((col) => (
          <Droppable droppableId={col} key={col} isDropDisabled={!canWrite}>
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`min-w-[300px] w-[300px] flex-shrink-0 border-r border-b border-border ${snapshot.isDraggingOver ? "bg-slate-50" : "bg-[#FAFAFA]"}`}
              >
                <div className="sticky top-0 z-10 bg-white border-b border-border px-4 py-3 flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-slate-700">{STATUSES[col].label}</span>
                  <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded-sm text-slate-500">{grouped[col].length}</span>
                </div>
                <div className="p-2 space-y-2 min-h-[120px]">
                  {grouped[col].map((d, idx) => (
                    <Draggable draggableId={d.id} index={idx} key={d.id} isDragDisabled={!canWrite}>
                      {(prov, snap) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          {...prov.dragHandleProps}
                          onClick={() => onCardClick(d)}
                          data-testid={`kanban-card-${d.dev_id}`}
                          className={`bg-white border border-slate-200 rounded-sm p-3 cursor-pointer relative hover:border-slate-900 transition-colors duration-200 ${snap.isDragging ? "shadow-[4px_4px_0_0_rgba(0,0,0,0.2)] opacity-90" : ""}`}
                          style={{ ...prov.draggableProps.style }}
                        >
                          <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: PRIORITIES[d.priority]?.dot }} />
                          <div className="pl-2">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="font-mono text-[10px] text-slate-400">{d.dev_id}</span>
                              <PriorityBadge priority={d.priority} />
                            </div>
                            <div className="text-sm font-semibold text-slate-900 leading-snug mb-2 line-clamp-2">{d.title}</div>
                            {(d.sistema || d.modulo) && (
                              <div className="flex items-center gap-1 text-[10px] text-slate-500 mb-2 flex-wrap">
                                {d.sistema && <span className="font-semibold text-slate-700">{d.sistema}</span>}
                                {d.modulo && <span className="text-slate-300">›</span>}
                                {d.modulo && <span>{d.modulo}</span>}
                                {d.submodulo && <span className="text-slate-300">›</span>}
                                {d.submodulo && <span>{d.submodulo}</span>}
                              </div>
                            )}
                            <ProgressBar value={d.avance} color={PRIORITIES[d.priority]?.dot} height={4} />
                            <div className="flex items-center justify-between mt-2.5">
                              <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                                {(d.responsables && d.responsables.length > 0) ? (
                                  <div className="flex -space-x-1.5">
                                    {d.responsables.slice(0, 3).map((r) => (
                                      <div key={r.id} title={r.name} className="ring-2 ring-white rounded-full"><Avatar user={r} size={20} /></div>
                                    ))}
                                    {d.responsables.length > 3 && <span className="text-[10px] text-slate-400 ml-2 self-center">+{d.responsables.length - 3}</span>}
                                  </div>
                                ) : <span className="text-slate-300">—</span>}
                                <span className="font-mono">{d.avance}%</span>
                              </div>
                              <div className="flex items-center gap-1">
                                {(d.correcciones || []).filter((c) => c.estado === "pendiente").length > 0 && (
                                  <span className="flex items-center gap-0.5 text-[10px] font-bold text-orange-600" title="Correcciones pendientes">
                                    <Bug className="h-3.5 w-3.5" />{(d.correcciones || []).filter((c) => c.estado === "pendiente").length}
                                  </span>
                                )}
                                {d.flags?.blocked && <Lock className="h-3.5 w-3.5 text-orange-500" title="Bloqueado" />}
                                {d.flags?.stale && <Clock className="h-3.5 w-3.5 text-amber-500" title="Sin movimiento" />}
                                {isOverdue(d.fecha_compromiso, d.status) && <AlertTriangle className="h-3.5 w-3.5 text-red-500" title="Vencido" />}
                                <span className={`text-[10px] font-mono ${isOverdue(d.fecha_compromiso, d.status) ? "text-red-600 font-bold" : "text-slate-400"}`}>{fmtDate(d.fecha_compromiso)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              </div>
            )}
          </Droppable>
        ))}
      </div>
    </DragDropContext>
  );
}
